import { useState } from "react";
import { Tab, Tabs } from "@mui/material";
import { ArrowRight, Check, ChevronDown } from "lucide-react";
import { RavenIcon } from "@ravenappdev/raven-ui";
import { Box, Button, Select, Stack, Typography } from "../ds";
import {
  IMPORT_FIELDS,
  mappedValue,
  resolveImportValue,
  type ColumnMapping,
  type ImportField,
  type ImportSheet,
  type ValueResolutions,
} from "../utils/suggestionImport";

const ROW_GRID = {
  xs: "minmax(0, 1fr)",
  sm: "minmax(0, 1fr) 20px minmax(0, 0.8fr)",
};
const PANEL_SX = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 2,
  overflow: "hidden",
};
const SUMMARY_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  cursor: "pointer",
  p: 1.5,
  listStyle: "none",
  "&::-webkit-details-marker": { display: "none" },
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: -2,
  },
  "details[open] > & .disclosure-icon": { transform: "rotate(180deg)" },
};
function DirectionArrow() {
  return (
    <RavenIcon
      icon={ArrowRight}
      sx={{ color: "text.secondary", display: { xs: "none", sm: "block" } }}
    />
  );
}
function MappingHeaders({ source }: { source: string }) {
  return (
    <Box
      sx={{
        display: { xs: "none", sm: "grid" },
        gridTemplateColumns: ROW_GRID,
        gap: 1.5,
        px: 1.5,
        py: 1,
        bgcolor: "background.default",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {source}
      </Typography>
      <Box />
      <Typography variant="caption" color="text.secondary">
        ESS field
      </Typography>
    </Box>
  );
}

interface ColumnsProps {
  sheet: ImportSheet;
  mapping: ColumnMapping;
  busy: boolean;
  attempted: boolean;
  notice: string;
  onChange: (field: ImportField, column: string) => void;
}
export function ImportColumnMatching({
  sheet,
  mapping,
  busy,
  attempted,
  notice,
  onChange,
}: ColumnsProps) {
  const required = IMPORT_FIELDS.filter((field) => field.required);
  const optional = IMPORT_FIELDS.filter((field) => !field.required);
  const renderRow = (field: (typeof IMPORT_FIELDS)[number]) => {
    const sample = sheet.rows
      .map((row) => String(mappedValue(row, mapping, field.key)))
      .find((value) => value.trim());
    return (
      <Box
        key={field.key}
        sx={{
          display: "grid",
          gridTemplateColumns: ROW_GRID,
          alignItems: "center",
          gap: { xs: 1, sm: 1.5 },
          px: 1.5,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:last-child": { borderBottom: 0 },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Select
            value={mapping[field.key] ?? ""}
            disabled={busy}
            error={attempted && field.required && !mapping[field.key]}
            label={`Excel column for ${field.label}`}
            slotProps={{ input: { notched: false } }}
            sx={{
              "& .MuiInputLabel-root": {
                width: 1,
                height: 1,
                p: 0,
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
              },
            }}
            options={[
              { value: "", label: "Choose an Excel column" },
              ...sheet.columns.map((value) => ({ value, label: value })),
            ]}
            onChange={(value) => onChange(field.key, value)}
          />
        </Box>
        <DirectionArrow />
        <Stack
          spacing={0.25}
          sx={{ gridRow: { xs: 1, sm: "auto" }, minWidth: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={600}>
              {field.label}
            </Typography>
            {mapping[field.key] && (
              <RavenIcon
                icon={Check}
                sx={{ fontSize: 16, color: "success.main" }}
              />
            )}
          </Stack>
          {sample && (
            <Typography
              variant="caption"
              color="text.secondary"
              title={sample}
              noWrap
              sx={{ display: "block", mt: 0.5 }}
            >
              Example: {sample}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  };
  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="h6">Match your columns</Typography>
        <Typography variant="body2" color="text.secondary">
          Choose a source column for each ESS field. Check the examples below.
        </Typography>
        {notice && !notice.startsWith("Columns matched automatically") && (
          <Typography variant="caption" color="text.secondary" role="status">
            {notice}
          </Typography>
        )}
      </Stack>
      <Box sx={PANEL_SX}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 1.5 }}
        >
          <Typography variant="subtitle2">Required fields</Typography>
          <Typography
            variant="caption"
            color={
              required.every((field) => mapping[field.key])
                ? "success.main"
                : "text.secondary"
            }
          >
            {required.filter((field) => mapping[field.key]).length} of{" "}
            {required.length} matched
          </Typography>
        </Stack>
        <MappingHeaders source="Excel column" />
        {required.map(renderRow)}
      </Box>
      <Box component="details" sx={PANEL_SX}>
        <Box component="summary" sx={SUMMARY_SX}>
          <Typography variant="subtitle2">Optional fields</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {optional.filter((field) => mapping[field.key]).length} of{" "}
              {optional.length} matched
            </Typography>
            <RavenIcon icon={ChevronDown} className="disclosure-icon" />
          </Stack>
        </Box>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 1.5, pb: 1.5 }}
        >
          Review these to preserve original numbers, dates, status and notes.
          Unmapped fields use the import defaults.
        </Typography>
        <MappingHeaders source="Excel column" />
        {optional.map(renderRow)}
      </Box>
    </Stack>
  );
}

type ValueField = "department" | "category" | "status";
interface ValuesProps {
  sheet: ImportSheet;
  mapping: ColumnMapping;
  resolutions: ValueResolutions;
  options: Record<ValueField, { value: string; label: string }[]>;
  onChange: (key: string, value: string) => void;
  onEditColumns: () => void;
  disabled?: boolean;
}
const FIELD_LABELS: Record<ValueField, string> = {
  department: "Department",
  category: "Category",
  status: "Status",
};
export function ImportValueReview({
  sheet,
  mapping,
  resolutions,
  options,
  onChange,
  onEditColumns,
  disabled = false,
}: ValuesProps) {
  const [activeField, setActiveField] = useState<ValueField>("department");
  const groups = (["department", "category", "status"] as const).map(
    (field) => {
      const counts = new Map<string, number>();
      sheet.rows.forEach((row) => {
        const value = String(mappedValue(row, mapping, field)).trim();
        counts.set(value, (counts.get(value) ?? 0) + 1);
      });
      const values = Array.from(counts, ([raw, count]) => ({
        raw,
        count,
        key: `${field}:${raw}`,
        match: resolveImportValue(field, raw, resolutions, options[field]),
      }));
      return {
        field,
        values,
        unmatched: values.filter((value) => !value.match),
        matched: values.filter((value) => !!value.match),
      };
    },
  );
  const unmatchedCount = groups.reduce(
    (total, group) => total + group.unmatched.length,
    0,
  );
  const renderRow = (
    field: ValueField,
    row: (typeof groups)[number]["values"][number],
  ) => {
    return (
      <Box
        key={row.key}
        sx={{
          display: "grid",
          gridTemplateColumns: ROW_GRID,
          gap: 1.5,
          p: 1.5,
          alignItems: "center",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            title={row.raw || "Blank value"}
            sx={{ overflowWrap: "anywhere" }}
          >
            {row.raw || "Blank value"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.count} {row.count === 1 ? "row" : "rows"} in Excel
          </Typography>
        </Box>
        <DirectionArrow />
        <Select
          disabled={disabled}
          value={row.match}
          label={`ESS ${FIELD_LABELS[field].toLowerCase()}`}
          options={[{ value: "", label: "Leave unmatched" }, ...options[field]]}
          onChange={(value) => onChange(row.key, value)}
        />
      </Box>
    );
  };
  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1}
        >
          <Typography variant="h6">Review your values</Typography>
          <Button size="small" variant="outlined" onClick={onEditColumns} disabled={disabled}>
            Edit column mapping
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {unmatchedCount
            ? `${unmatchedCount} distinct values need a match. Choose an ESS value, or leave them unmatched to exclude those rows.`
            : "All values have a match. Expand a section to check or change them."}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Each choice applies to every row with that Excel value.
        </Typography>
      </Stack>
      <Tabs
        value={activeField}
        onChange={(_, value: ValueField) => {
          setActiveField(value);
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        aria-label="Values to review"
        sx={{ borderBottom: "1px solid", borderColor: "divider" }}
      >
        {groups.map((group) => (
          <Tab
            key={group.field}
            value={group.field}
            id={`import-tab-${group.field}`}
            aria-controls={`import-panel-${group.field}`}
            label={`${FIELD_LABELS[group.field]}${group.unmatched.length ? ` (${group.unmatched.length})` : ""}`}
            sx={{ textTransform: "none" }}
          />
        ))}
      </Tabs>
      {groups
        .filter((group) => group.field === activeField)
        .map((group) => (
          <Box
            key={group.field}
            sx={PANEL_SX}
            role="tabpanel"
            id={`import-panel-${group.field}`}
            aria-labelledby={`import-tab-${group.field}`}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{ p: 1.5, bgcolor: "background.default" }}
            >
              <Typography variant="subtitle2">
                {FIELD_LABELS[group.field]}
              </Typography>
              <Typography
                variant="caption"
                color={group.unmatched.length ? "warning.dark" : "success.main"}
              >
                {group.unmatched.length
                  ? `${group.unmatched.length} need a match`
                  : "All matched"}
              </Typography>
            </Stack>
            {group.unmatched.map((row) => renderRow(group.field, row))}
            {group.matched.length > 0 && (
              <Box component="details">
                <Box component="summary" sx={SUMMARY_SX}>
                  <Typography variant="body2" color="text.secondary">
                    Review or edit {group.matched.length} matched{" "}
                    {group.matched.length === 1 ? "value" : "values"}
                  </Typography>
                  <RavenIcon icon={ChevronDown} className="disclosure-icon" />
                </Box>
                {group.matched.map((row) => renderRow(group.field, row))}
              </Box>
            )}
          </Box>
        ))}
    </Stack>
  );
}
